import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertTriangle,
  Camera,
  Download,
  Loader2,
  LockKeyhole,
  Mail,
  RotateCcw,
  Shield,
  X,
} from "lucide-react";
import { ConvexError } from "convex/values";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import {
  Avatar,
  Button,
  ConfirmDeleteDialog,
  Input,
  PasswordInput,
} from "@/components/ui";
import { useProfile, useApiMutation } from "@/packages/features-shared";
import { useTranslation, i18n } from "@/packages/utils";
import { useWorkspace } from "@/routes/$workspaceSlug/route";
import { useWorkspaceId } from "@/hooks/use-workspace-mutation";
import {
  SettingsActionBar,
  SettingsField,
  SettingsFieldGroup,
  SettingsPageHeader,
  SettingsPanel,
} from "./SettingsSection";
import { toast } from "sonner";

function getProfileSchema() {
  return z.object({
    name: z.string().min(1, i18n.t("settings:profile.validation.nameRequired")),
    jobTitle: z.string().optional(),
    companyName: z.string().optional(),
  });
}

function getPasswordSchema() {
  return z
    .object({
      currentPassword: z
        .string()
        .min(1, i18n.t("settings:profile.validation.currentPasswordRequired")),
      newPassword: z
        .string()
        .min(8, i18n.t("settings:profile.validation.minLength")),
      confirmPassword: z
        .string()
        .min(1, i18n.t("settings:profile.validation.confirmPasswordRequired")),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: i18n.t("settings:profile.validation.passwordsDontMatch"),
      path: ["confirmPassword"],
    });
}

type ProfileFormValues = z.infer<ReturnType<typeof getProfileSchema>>;
type PasswordFormValues = z.infer<ReturnType<typeof getPasswordSchema>>;

export function ProfileSection() {
  const { t } = useTranslation(["settings"]);
  const { user, isLoading, updateProfile } = useProfile();
  const { workspace } = useWorkspace();
  const workspaceId = useWorkspaceId();
  const updateWorkspace = useMutation(api.workspaces.update);
  const initializedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const generateUploadUrl = useMutation(
    api.document_mutations.generateUploadUrl,
  );
  const uploadAvatarMutation = useApiMutation(api.users.uploadAvatar, {
    name: "upload-avatar",
    successMessage: t("profile.avatar.updated") as string,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(getProfileSchema()),
    defaultValues: {
      name: "",
      jobTitle: "",
      companyName: "",
    },
  });

  useEffect(() => {
    if (user && !initializedRef.current) {
      initializedRef.current = true;
      reset({
        name: user.name,
        jobTitle: user.jobTitle ?? "",
        companyName: workspace?.companyName ?? "",
      });
    }
  }, [user, workspace, reset]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingAvatar(true);

      const uploadUrl = await generateUploadUrl({});
      const uploadResult = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!uploadResult.ok) {
        throw new Error(`Upload failed: ${uploadResult.statusText}`);
      }

      const { storageId } = (await uploadResult.json()) as {
        storageId: Id<"_storage">;
      };

      await uploadAvatarMutation.mutateAsync({ storageId });
    } catch (error) {
      console.error("Avatar upload failed:", error);
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleReset = () => {
    reset({
      name: user?.name ?? "",
      jobTitle: user?.jobTitle ?? "",
      companyName: workspace?.companyName ?? "",
    });
  };

  const onSubmit = async (values: ProfileFormValues) => {
    await updateProfile.mutateAsync({
      name: values.name,
      jobTitle: values.jobTitle || undefined,
    });
    // Save companyName to workspace if it changed
    const newCompanyName = values.companyName ?? "";
    const currentCompanyName = workspace?.companyName ?? "";
    if (newCompanyName !== currentCompanyName) {
      await updateWorkspace({
        workspaceId,
        companyName: newCompanyName || undefined,
      });
    }
    reset(values);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <SettingsPageHeader
        eyebrow={t("sections.account") as string}
        title={t("profile.title")}
        description={t("profile.description")}
      />

      <SettingsPanel
        // title={t("profile.panels.personal.title") as string}
        // description={
        //   t("profile.panels.personal.description") as string
        // }
        contentClassName="space-y-8"
      >
        <div className="rounded-[20px] border border-accent/15 bg-[linear-gradient(135deg,var(--color-accent-soft),rgba(255,255,255,0.92))] p-5 sm:p-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar
                name={user?.name ?? (t("profile.defaultName") as string)}
                avatarUrl={user?.imageUrl}
                avatarColor={user?.avatarColor ?? "#E8734A"}
                size="lg"
                className="h-24! w-24! text-[26px]!"
              />
              <button
                type="button"
                onClick={handleAvatarClick}
                disabled={isUploadingAvatar}
                className="absolute -bottom-0.5 -right-0.5 flex h-7 w-7 items-center justify-center rounded-full border border-white/80 bg-white text-primary shadow-sm transition-colors hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-60"
                aria-label={t("profile.avatar.change") as string}
              >
                {isUploadingAvatar ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Camera size={13} />
                )}
              </button>
            </div>

            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
                {t("profile.badge")}
              </p>
              <h2 className="mt-1 truncate text-[24px] font-semibold tracking-[-0.03em] text-primary">
                {user?.name ?? (t("profile.defaultName") as string)}
              </h2>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-secondary">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/80 bg-white/75 px-3 py-1">
                  <Mail size={13} />
                  {user?.email ?? ""}
                </span>
                {user?.jobTitle ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/80 bg-white/75 px-3 py-1">
                    <Shield size={13} />
                    {user.jobTitle}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            className="hidden"
          />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <SettingsFieldGroup>
            <SettingsField
              label={t("profile.fields.fullName") as string}
              description={t("profile.fullNameHelp") as string}
              error={errors.name?.message}
            >
              <Input
                {...register("name")}
                placeholder={t("profile.fields.fullName") as string}
              />
            </SettingsField>

            <ReadOnlyField
              label={t("profile.fields.email") as string}
              description={t("profile.emailHelp") as string}
              value={user?.email ?? ""}
              icon={<Mail size={16} className="text-muted" />}
            />
          </SettingsFieldGroup>

          <SettingsFieldGroup>
            <SettingsField
              label={t("profile.fields.jobTitle") as string}
              description={t("profile.jobTitleHelp") as string}
            >
              <Input
                {...register("jobTitle")}
                placeholder={t("profile.fields.jobTitle") as string}
              />
            </SettingsField>

            <SettingsField
              label={t("profile.fields.companyName") as string}
              description={t("profile.companyNameHelp") as string}
            >
              <Input
                {...register("companyName")}
                placeholder={t("profile.fields.companyName") as string}
              />
            </SettingsField>
          </SettingsFieldGroup>

          <SettingsActionBar
            hint={
              isDirty
                ? (t("profile.actions.unsaved") as string)
                : (t("profile.actions.noChanges") as string)
            }
            className="rounded-[18px] border border-border/80 bg-white/70 px-4 py-3"
          >
            <Button
              type="button"
              variant="ghost"
              icon={RotateCcw}
              disabled={!isDirty || updateProfile.isPending}
              onClick={handleReset}
            >
              {t("profile.actions.reset")}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!isDirty || updateProfile.isPending}
              loading={updateProfile.isPending}
            >
              {t("profile.actions.saveChanges")}
            </Button>
          </SettingsActionBar>
        </form>
      </SettingsPanel>

      <ChangePasswordSection />
      <AccountDangerZone />
    </div>
  );
}


function ChangePasswordSection() {
  const { t } = useTranslation(["settings"]);
  const changePassword = useAction(api.auth.changePassword);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(getPasswordSchema()),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: PasswordFormValues) => {
    setIsSubmitting(true);
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success(t("profile.changePassword.success") as string);
      reset();
    } catch (error) {
      let message = t("profile.changePassword.failed") as string;
      // ConvexError surfaces structured data with a message field
      if (error instanceof ConvexError) {
        const data = error.data;
        if (
          typeof data === "object" &&
          data !== null &&
          "message" in data &&
          typeof (data as { message: unknown }).message === "string"
        ) {
          message = (data as { message: string }).message;
        }
      }
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SettingsPanel
      title={t("profile.changePassword.title") as string}
      description={t("profile.changePassword.description") as string}
      contentClassName="space-y-6"
    >
      <div className="flex items-start gap-3 rounded-[18px] border border-border/80 bg-white/70 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-accent-soft text-accent">
          <LockKeyhole size={18} />
        </div>
        <div>
          <p className="text-[14px] font-medium text-primary">
            {t("profile.changePassword.title")}
          </p>
          <p className="mt-1 text-[13px] leading-6 text-secondary">
            {t("profile.changePassword.helper")}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="max-w-[520px] space-y-5">
          <SettingsField
            label={t("profile.changePassword.currentPassword") as string}
            error={errors.currentPassword?.message}
          >
            <PasswordInput
              placeholder={
                t(
                  "settings:profile.changePassword.currentPasswordPlaceholder",
                ) as string
              }
              {...register("currentPassword")}
            />
          </SettingsField>

          <SettingsField
            label={t("profile.changePassword.newPassword") as string}
            error={errors.newPassword?.message}
          >
            <PasswordInput
              placeholder={
                t(
                  "settings:profile.changePassword.newPasswordPlaceholder",
                ) as string
              }
              {...register("newPassword")}
            />
          </SettingsField>

          <SettingsField
            label={t("profile.changePassword.confirmPassword") as string}
            error={errors.confirmPassword?.message}
          >
            <PasswordInput
              placeholder={
                t(
                  "settings:profile.changePassword.confirmPasswordPlaceholder",
                ) as string
              }
              {...register("confirmPassword")}
            />
          </SettingsField>
        </div>

        <SettingsActionBar
          hint={
            isDirty ? (t("profile.changePassword.ready") as string) : undefined
          }
          className="rounded-[18px] border border-border/80 bg-white/70 px-4 py-3"
        >
          <Button
            type="button"
            variant="ghost"
            icon={RotateCcw}
            disabled={!isDirty || isSubmitting}
            onClick={() => reset()}
          >
            {t("profile.actions.reset")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            {t("profile.changePassword.submit")}
          </Button>
        </SettingsActionBar>
      </form>
    </SettingsPanel>
  );
}

function AccountDangerZone() {
  const { t } = useTranslation(["settings"]);
  const { user } = useProfile();
  const workspaces = useQuery(api.workspaces.getMyWorkspaces, {});
  const deleteAccount = useAction(api.user_action_delete.deleteAccount);
  const cancelDeletion = useMutation(api.user_deletion_schedule.cancelDeletion);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [exportRequested, setExportRequested] = useState(false);

  const exportData = useQuery(
    api.workspace_export.exportAccountData,
    exportRequested ? {} : "skip",
  );

  const downloadTriggeredRef = useRef(false);

  const handleExport = useCallback(() => {
    if (exportData) {
      triggerJsonDownload(exportData, "account-export.json");
      return;
    }
    downloadTriggeredRef.current = false;
    setExportRequested(true);
  }, [exportData]);

  const exportPending = exportRequested && !exportData;

  useEffect(() => {
    if (exportRequested && exportData && !downloadTriggeredRef.current) {
      downloadTriggeredRef.current = true;
      triggerJsonDownload(exportData, "account-export.json");
      toast.success(t("profile.dangerZone.exportDownloaded") as string);
    }
  }, [exportRequested, exportData, t]);

  const ownedWorkspaces = (workspaces ?? []).filter((w) => w.role === "owner");

  const scheduledAt = user?.deletionScheduledAt;
  const isPendingDeletion = !!scheduledAt;
  const deletionDate = scheduledAt
    ? new Date(scheduledAt + 7 * 24 * 60 * 60 * 1000)
    : null;
  const daysRemaining = deletionDate
    ? Math.ceil((deletionDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
    : 0;

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteAccount({});
      toast.success(t("profile.dangerZone.deletionScheduled") as string);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : (t("profile.dangerZone.failedScheduleDeletion") as string),
      );
    } finally {
      setDeleting(false);
    }
  }

  async function handleCancelDeletion() {
    setCancelling(true);
    try {
      await cancelDeletion({});
      toast.success(t("profile.dangerZone.deletionCancelled") as string);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : (t("profile.dangerZone.failedCancelDeletion") as string),
      );
    } finally {
      setCancelling(false);
    }
  }

  const userEmail = user?.email ?? "";

  const workspaceCountText =
    ownedWorkspaces.length > 0
      ? (t("profile.dangerZone.confirmWorkspacesMsg", {
          count: ownedWorkspaces.length,
        }) as string)
      : (t("profile.dangerZone.confirmNoWorkspacesMsg") as string);

  return (
    <SettingsPanel
      title={t("profile.dangerZone.title") as string}
      description={t("profile.dangerZone.description") as string}
      tone="danger"
      contentClassName="space-y-4"
    >
      {isPendingDeletion && deletionDate ? (
        <WarningCallout tone="warning">
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-red-900">
              {t("profile.dangerZone.pendingDeletionDate", {
                date: deletionDate.toLocaleDateString(),
              })}
            </p>
            <p className="mt-1 text-[13px] text-red-800">
              {t("profile.dangerZone.pendingDaysRemaining", {
                count: daysRemaining,
              })}
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={handleCancelDeletion}
            disabled={cancelling}
            loading={cancelling}
            className="text-red-900 hover:bg-red-100 hover:text-red-950"
          >
            <X className="mr-1 h-3.5 w-3.5" />
            {t("profile.dangerZone.cancelDeletion")}
          </Button>
        </WarningCallout>
      ) : null}

      {!isPendingDeletion && ownedWorkspaces.length > 0 ? (
        <WarningCallout tone="warning">
          <div>
            <p className="text-[14px] font-semibold text-red-900">
              {t("profile.dangerZone.workspacesWarning")}
            </p>
            <ul className="mt-2 space-y-1">
              {ownedWorkspaces.map((w) => (
                <li
                  key={w._id}
                  className="ml-4 list-disc text-[13px] text-red-800"
                >
                  {w.name}
                </li>
              ))}
            </ul>
          </div>
        </WarningCallout>
      ) : null}

      <DangerRow
        title={t("profile.dangerZone.exportTitle") as string}
        description={t("profile.dangerZone.exportDescription") as string}
        action={
          <Button
            variant="secondary"
            onClick={handleExport}
            disabled={deleting || exportPending}
            loading={exportPending}
          >
            <Download className="mr-1.5 h-4 w-4" />
            {t("profile.dangerZone.exportButton")}
          </Button>
        }
      />

      {!isPendingDeletion ? (
        <DangerRow
          title={t("profile.dangerZone.deleteTitle") as string}
          description={t("profile.dangerZone.deleteDescription") as string}
          action={
            <Button
              variant="danger"
              onClick={() => setShowConfirm(true)}
              disabled={deleting}
            >
              {t("profile.dangerZone.deleteAccountButton")}
            </Button>
          }
        />
      ) : null}

      <ConfirmDeleteDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleDelete}
        loading={deleting}
        title={t("profile.dangerZone.confirmTitle") as string}
        description={workspaceCountText}
        confirmMatchText={userEmail}
        confirmLabel={t("profile.dangerZone.confirmButton") as string}
      />
    </SettingsPanel>
  );
}

function ReadOnlyField({
  label,
  description,
  value,
  icon,
}: {
  label: string;
  description?: string;
  value: string;
  icon?: ReactNode;
}) {
  return (
    <SettingsField label={label} description={description}>
      <div className="flex min-h-[42px] items-center gap-3 rounded-[14px] border border-border/80 bg-[rgba(248,248,248,0.9)] px-3.5 py-2.5">
        {icon}
        <span className="truncate text-[14px] text-primary">{value}</span>
      </div>
    </SettingsField>
  );
}

function WarningCallout({
  children,
  tone,
}: {
  children: ReactNode;
  tone: "warning";
}) {
  const toneClass =
    tone === "warning"
      ? "border-red-200 bg-red-50/80"
      : "border-border bg-white/80";

  return (
    <div
      className={`flex flex-col gap-3 rounded-[18px] border p-4 sm:flex-row sm:items-start sm:justify-between ${toneClass}`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
        {children}
      </div>
    </div>
  );
}

function DangerRow({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-[18px] border border-red-100 bg-white/75 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-[14px] font-semibold text-primary">{title}</p>
        <p className="mt-1 text-[13px] leading-6 text-secondary">
          {description}
        </p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

function triggerJsonDownload(data: unknown, filename: string) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
